import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/api';
import { useHayCursosDisponibles, filterMenuCursosDisponibles } from '../hooks/useHayCursosDisponibles';
import { NOTAS_ACTUALIZACIONES } from '../pages/NotasVersion';
import UploadProgress from './UploadProgress';
import { AVATAR_MAX_BYTES, AVATAR_MAX_MB } from '../constants/uploadLimits';
import { formatPersonName } from '../utils/personName';
import './Sidebar.css';

const versionActual = NOTAS_ACTUALIZACIONES[0]?.version || '—';

const buildAvatarUrl = (path) => {
  if (!path) return null;
  const p = String(path).trim();
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const mediaBase = apiUrl ? apiUrl.replace(/\/api\/?$/, '') : '';
  const normalized = p.startsWith('/') ? p : `/media/${p}`;
  return mediaBase ? `${mediaBase}${normalized}` : normalized;
};

const SIDEBAR_PIN_KEY = 'campus_sidebar_pinned';

const Sidebar = ({ theme = 'light', onToggleTheme = () => {}, onPinnedChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const [selectedRole, setSelectedRole] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [alertPopup, setAlertPopup] = useState({ show: false, message: '' });
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_PIN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const hasInitialized = useRef(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (typeof onPinnedChange === 'function') {
      onPinnedChange(sidebarPinned);
    }
  }, [sidebarPinned, onPinnedChange]);

  // Cerrar menú móvil al cambiar de ruta
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen]);

  const isRoleEstudiante = selectedRole === 'Estudiante' || selectedRole === 'Estudiantes';
  const { hayCursos, loading: loadingCursosDisponibles } = useHayCursosDisponibles(isRoleEstudiante);
  const showCursosDisponibles = !isRoleEstudiante || (!loadingCursosDisponibles && hayCursos);

  useEffect(() => {
    // Solo ejecutar una vez al montar el componente
    if (hasInitialized.current) {
      return;
    }

    // Cargar grupos del usuario
    if (currentUser?.groups && currentUser.groups.length > 0) {
      setUserGroups(currentUser.groups);
      // Intentar cargar el rol seleccionado desde localStorage, si no existe usar el primero
      const savedRole = localStorage.getItem('selectedRole');
      const initialRole = savedRole && currentUser.groups.includes(savedRole) 
        ? savedRole 
        : currentUser.groups[0];
      setSelectedRole(initialRole);
      localStorage.setItem('selectedRole', initialRole);
      hasInitialized.current = true;
    } else {
      // Si no hay grupos, intentar obtenerlos del servidor
      authService.getGroups()
        .then(response => {
          if (response.groups && response.groups.length > 0) {
            const groupNames = response.groups.map(g => g.name);
            setUserGroups(groupNames);
            // Intentar cargar el rol seleccionado desde localStorage
            const savedRole = localStorage.getItem('selectedRole');
            const initialRole = savedRole && groupNames.includes(savedRole) 
              ? savedRole 
              : groupNames[0];
            setSelectedRole(initialRole);
            localStorage.setItem('selectedRole', initialRole);
          } else {
            setUserGroups(['Estudiante']);
            setSelectedRole('Estudiante');
            localStorage.setItem('selectedRole', 'Estudiante');
          }
          hasInitialized.current = true;
        })
        .catch(() => {
          setUserGroups(['Estudiante']);
          setSelectedRole('Estudiante');
          localStorage.setItem('selectedRole', 'Estudiante');
          hasInitialized.current = true;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  // Cargar avatar del perfil extendido
  useEffect(() => {
    authService.getProfileExtended()
      .then((profile) => {
        setAvatarUrl(buildAvatarUrl(profile.avatar));
      })
      .catch(() => {});
  }, []);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const ALLOWED_AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_AVATAR_TYPES.includes(file.type) || !['png', 'jpg', 'jpeg'].includes(ext)) {
      setAlertPopup({ show: true, message: 'La imagen debe ser .png, .jpg o .jpeg.' });
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setAlertPopup({ show: true, message: `La imagen no puede superar ${AVATAR_MAX_MB} MB.` });
      return;
    }
    setLoadingAvatar(true);
    setUploadProgress(0);
    try {
      const data = await authService.uploadAvatar(file, {
        onUploadProgress: (percent) => setUploadProgress(percent),
      });
      if (data.profile?.avatar) {
        setAvatarUrl(buildAvatarUrl(data.profile.avatar));
      }
      setUploadProgress(100);
    } catch (err) {
      console.error('Error al subir avatar:', err);
      const msg = err.response?.data?.avatar?.[0] || err.response?.data?.detail || err.message || 'No se pudo subir la imagen.';
      setAlertPopup({ show: true, message: typeof msg === 'string' ? msg : 'No se pudo subir la imagen.' });
    } finally {
      setLoadingAvatar(false);
      setTimeout(() => setUploadProgress(0), 400);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate('/');
  };

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    setSelectedRole(newRole);
    localStorage.setItem('selectedRole', newRole);
    window.dispatchEvent(new CustomEvent('roleChanged', { detail: { role: newRole } }));
    // Ir siempre al dashboard para no quedar en una vista del rol anterior
    navigate('/dashboard');
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  // Función para obtener el menú según el rol seleccionado
  const getMenuItems = useMemo(() => {
    const baseMenu = [
      { path: '/dashboard', icon: '🏠', label: 'Panel Principal' },
      { path: '/dashboard/perfil', icon: '👤', label: 'Mi Perfil' },
      { path: '/dashboard/chat', icon: '💬', label: 'Chat' },
    ];

    // Menú específico según el rol
    switch (selectedRole) {
      case 'Coordinador':
        return [
          ...baseMenu,
          { path: '/dashboard/usuarios', icon: '👥', label: 'Gestión de Usuarios' },
          { path: '/dashboard/cursos', icon: '📚', label: 'Cursos' },
          { path: '/dashboard/certificados', icon: '📜', label: 'Certificados' },
          { path: '/dashboard/configuracion', icon: '⚙️', label: 'Configuración' },
          { path: '/dashboard/estadisticas', icon: '📈', label: 'Estadísticas' },
        ];
      case 'Profesor':
        return [
          ...baseMenu,
          { path: '/dashboard/cursos', icon: '📚', label: 'Cursos' },
          { path: '/dashboard/inscripcion', icon: '✍️', label: 'Inscripción' },
          { path: '/dashboard/clases', icon: '🎓', label: 'Clases' },
          { path: '/dashboard/tareas', icon: '📝', label: 'Tareas' },
          { path: '/dashboard/grupos', icon: '👥', label: 'Grupos' },
          { path: '/dashboard/asistencia', icon: '📋', label: 'Asistencia' },
          { path: '/dashboard/reuniones', icon: '💻', label: 'Reuniones' },
          { path: '/dashboard/estudiantes', icon: '👨‍🎓', label: 'Estudiantes' },
        ];
      case 'Tutor':
        return [
          ...baseMenu,
          { path: '/dashboard/cursos', icon: '📚', label: 'Cursos' },
          { path: '/dashboard/clases', icon: '🎓', label: 'Clases' },
          { path: '/dashboard/tareas', icon: '📝', label: 'Tareas' },
          { path: '/dashboard/grupos', icon: '👥', label: 'Grupos' },
          { path: '/dashboard/asistencia', icon: '📋', label: 'Asistencia' },
          { path: '/dashboard/reuniones', icon: '💻', label: 'Reuniones' },
        ];
      case 'Pasante':
        return [
          ...baseMenu,
          { path: '/dashboard/tareas', icon: '📝', label: 'Tareas' },
          { path: '/dashboard/grupos', icon: '👥', label: 'Grupos' },
          { path: '/dashboard/asistencia', icon: '📋', label: 'Asistencia' },
        ];
      case 'Ayudante':
        return [
          ...baseMenu,
          { path: '/dashboard/tareas', icon: '📝', label: 'Tareas' },
          { path: '/dashboard/grupos', icon: '👥', label: 'Grupos' },
          { path: '/dashboard/asistencia', icon: '📋', label: 'Asistencia' },
        ];
      default: // Estudiante o cualquier otro rol
        return [
          ...baseMenu,
          { path: '/dashboard/cursos-disponibles', icon: '📖', label: 'Cursos disponibles' },
          { path: '/dashboard/mis-cursos', icon: '📚', label: 'Mis Cursos' },
          { path: '/dashboard/mis-grupos', icon: '👥', label: 'Mis grupos' },
          { path: '/dashboard/mi-asistencia', icon: '📋', label: 'Mi Asistencia' },
          { path: '/dashboard/calificaciones', icon: '📊', label: 'Correcciones	' },
          { path: '/dashboard/mi-configuracion', icon: '⚙️', label: 'Configuración' },
        ];
    }
  }, [selectedRole]);

  const visibleMenuItems = useMemo(
    () => filterMenuCursosDisponibles(getMenuItems, showCursosDisponibles),
    [getMenuItems, showCursosDisponibles]
  );

  const toggleSidebarPin = () => {
    setSidebarPinned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_PIN_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <>
    <header className="mobile-topbar">
      <button
        type="button"
        className="mobile-topbar-burger"
        onClick={() => setMobileOpen(true)}
        aria-label="Abrir menú"
        aria-expanded={mobileOpen}
        aria-controls="dashboard-sidebar"
      >
        <span className="mobile-topbar-burger-lines" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>
      <div className="mobile-topbar-brand">
        <img src={`${process.env.PUBLIC_URL || ''}/images/icons3f.png`} alt="" className="mobile-topbar-logo" />
        <span className="mobile-topbar-title">Tecno3F</span>
      </div>
      <button
        type="button"
        className="mobile-topbar-theme"
        onClick={onToggleTheme}
        aria-label={theme === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
      >
        {theme === 'dark' ? '☀️' : '🌙'}
      </button>
    </header>

    <div
      className={`sidebar-backdrop${mobileOpen ? ' sidebar-backdrop--open' : ''}`}
      onClick={() => setMobileOpen(false)}
      aria-hidden={!mobileOpen}
    />

    <div
      id="dashboard-sidebar"
      className={`sidebar${sidebarPinned ? ' sidebar--pinned' : ''}${mobileOpen ? ' sidebar--mobile-open' : ''}`}
    >
      <div className="sidebar-header">
        <img src={`${process.env.PUBLIC_URL || ''}/images/icons3f.png`} alt="Tecno3F" className="sidebar-logo-img" />
        <h2>Tecno3F</h2>
        <button
          type="button"
          className="sidebar-mobile-close"
          onClick={() => setMobileOpen(false)}
          aria-label="Cerrar menú"
        >
          ✕
        </button>
        <button
          type="button"
          className={`sidebar-pin-btn${sidebarPinned ? ' active' : ''}`}
          onClick={toggleSidebarPin}
          title={sidebarPinned ? 'Desfijar menú (se oculta al salir)' : 'Fijar menú abierto'}
          aria-label={sidebarPinned ? 'Desfijar menú lateral' : 'Fijar menú lateral'}
          aria-pressed={sidebarPinned}
        >
          {sidebarPinned ? (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M14 4v7.5l2 1.5V14h-3.5v6h-1V14H8v-1l2-1.5V4H8V2h8v2h-2z" opacity="0.95" />
            </svg>
          )}
        </button>
      </div>
      
      <div className="sidebar-user">
        <div className="sidebar-user-info">
          <div className="sidebar-user-avatar-wrap" onClick={handleAvatarClick} role="button" tabIndex={0} onKeyDown={(ev) => ev.key === 'Enter' && handleAvatarClick()} aria-label="Cambiar foto de perfil">
            <div className={`sidebar-user-avatar ${avatarUrl ? 'sidebar-user-avatar--img' : ''}`}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="sidebar-user-avatar-img" />
              ) : (
                formatPersonName(currentUser?.first_name)?.charAt(0) || 'U'
              )}
            </div>
            {loadingAvatar && (
              <div className="sidebar-user-avatar-progress">
                <UploadProgress percent={uploadProgress} label={uploadProgress >= 100 ? 'Listo' : `Subiendo… ${uploadProgress}%`} />
              </div>
            )}
            <span className="sidebar-user-avatar-edit" aria-hidden>✎</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg"
              className="sidebar-user-avatar-input"
              onChange={handleAvatarFileChange}
              aria-hidden
            />
          </div>
          <div className="sidebar-user-details">
            <p className="sidebar-user-name">{formatPersonName(currentUser?.first_name) || 'Usuario'}</p>
            {userGroups.length > 1 ? (
              <select
                className="sidebar-role-select"
                value={selectedRole || ''}
                onChange={handleRoleChange}
              >
                {userGroups.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            ) : (
              <p className="sidebar-user-role">
                {selectedRole || userGroups[0] || 'Estudiante'}
              </p>
            )}
          </div>
        </div>
      </div>

      <nav className="sidebar-nav">
        <ul className="sidebar-menu">
          {visibleMenuItems.map((item) => (
            <li key={item.path}>
              <Link 
                to={item.path} 
                className={`sidebar-link ${isActive(item.path)}`}
              >
                <span className="sidebar-icon">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <button
          type="button"
          className="sidebar-theme-toggle"
          onClick={onToggleTheme}
          aria-label={theme === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
        >
          <span className="sidebar-icon">{theme === 'dark' ? '☀️' : '🌙'}</span>
          <span>{theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}</span>
        </button>
        <button onClick={handleLogout} className="sidebar-logout">
          <span className="sidebar-icon">🚪</span>
          <span>Cerrar Sesión</span>
        </button>
        <p className="sidebar-version" aria-label="Versión actual">V&nbsp;{versionActual}</p>
      </div>
    </div>

    {alertPopup.show && (
      <div className="sidebar-alert-overlay" onClick={() => setAlertPopup({ show: false, message: '' })} role="dialog" aria-modal="true" aria-labelledby="sidebar-alert-title">
        <div className="sidebar-alert-popup" onClick={(e) => e.stopPropagation()}>
          <p id="sidebar-alert-title" className="sidebar-alert-message">{alertPopup.message}</p>
          <button type="button" className="sidebar-alert-btn" onClick={() => setAlertPopup({ show: false, message: '' })}>
            Aceptar
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default Sidebar;


